import React, { useMemo } from 'react';

import { DropdownMenu, MenuItem } from '@reckon-web/dropdown';
import { Button } from '@reckon-web/button';
import {
  CheckCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CornerUpLeftIcon,
  MailIcon,
  Trash2Icon,
  XIcon,
} from '@reckon-web/icon';
import { LoadingDots } from '@reckon-web/loading';
import { makeId } from '@reckon-web/utils';

import { useGetMyDetails } from '../../../data/useGetMyDetails';
import { useJobSignOffApprove } from '../../../data/useJobSignOffApprove';
import { Job } from '../../../hooks/use-query-job';
import { useJobSignOffRequest } from '../../../hooks/use-job-sign-off-request';
import { useJobSignOffWithdraw } from '../../../hooks/use-job-sign-off-withdraw';
import { useJobSignOffDecline } from '../../../hooks/use-job-sign-off-decline';
import { useJobSignOffRevoke } from '../../../hooks/use-job-sign-off-revoke';
import { useUpdateJobStatus } from '../../../hooks/use-update-job-status';
import { useDeleteJob } from '../../../hooks/use-delete-job';
import { DisabledElementTooltip } from '../../common/disabled-element-tooltip';
import {
  canApproveSignOff,
  canDeclineSignOff,
  canDeleteJob,
  canEditJob,
  canRequestSignOff,
  canRevokeSignOff,
  canWithdrawSignOff,
} from '../../../utils/user-security-utils';
import { ViewJobDialogDoCloseArgs } from '../view-job-dialog';
import { useGetJobStatusList } from '../../../data/useGetJobStatusList';
import {
  JobSignOffStatus,
  JobSignOffStatusType,
} from '../../../hooks/use-query-signoffs';
import { JobStatusEnum } from '../types/job-status-enum';

export const Options: React.FC<{
  job: Job;
  testId?: string;
  doClose: (args: ViewJobDialogDoCloseArgs) => void;
}> = ({ job, doClose, testId = 'options' }) => {
  const [JobSignOffRequestDialog, jobSignOffRequest] = useJobSignOffRequest();
  const [JobSignOffWithdrawDialog, jobSignOffWithdraw] =
    useJobSignOffWithdraw();
  const [jobSignOffApprove] = useJobSignOffApprove();
  const [JobSignOffDeclineDialog, jobSignOffDecline] = useJobSignOffDecline();
  const [JobSignOffRevokeDialog, jobSignOffRevoke] = useJobSignOffRevoke();
  const [UpdateJobStatusDialog, updateJobStatus] = useUpdateJobStatus();
  const [DeleteJobDialog, deleteJob] = useDeleteJob();

  const [isPending, setIsPending] = React.useState<boolean>();

  const { loading, error, data: currentUser } = useGetMyDetails();
  const {
    loading: loadingStatuses,
    error: errorStatuses,
    data: statuses,
  } = useGetJobStatusList();
  const closedJobStatus = useMemo(
    () => statuses?.find((x) => x.status === JobStatusEnum.valuesEnum.Closed),
    [statuses]
  );

  // TODO
  if (loading || loadingStatuses) return <div>Loading...</div>;
  if (error || errorStatuses) return <div>Error!</div>;
  if (!currentUser || !closedJobStatus) return <div>Error!</div>;

  const { user } = currentUser;

  if (
    !job.signOffStatus ||
    !user ||
    !user.active ||
    !user.licensed ||
    !user.workflowRole
  )
    return <div>Error!</div>;

  const isJobClosed = job.status.status === JobStatusEnum.valuesEnum.Closed;
  const requestedAndApprovedStatus: JobSignOffStatusType[] = [
    JobSignOffStatus.Requested,
    JobSignOffStatus.Approved,
  ];

  const showRequestSignOff =
    !isJobClosed &&
    !requestedAndApprovedStatus.includes(job.signOffStatus) &&
    canRequestSignOff(user);
  const showWithdrawSignOff =
    !isJobClosed &&
    job.signOffStatus === JobSignOffStatus.Requested &&
    canWithdrawSignOff(user);
  const showApproveSignOff =
    !isJobClosed &&
    job.signOffStatus === JobSignOffStatus.Requested &&
    canApproveSignOff(user);
  const showDeclineSignOff =
    !isJobClosed &&
    job.signOffStatus === JobSignOffStatus.Requested &&
    canDeclineSignOff(user);
  const showRevokeSignOff =
    !isJobClosed &&
    job.signOffStatus === JobSignOffStatus.Approved &&
    canRevokeSignOff(user);
  const showCloseJob = !isJobClosed && canEditJob(user);
  const showDeleteJob =
    !isJobClosed && !job.WfDeletedOnUTC && canDeleteJob(user);

  const hasMenuItem =
    showRequestSignOff ||
    showWithdrawSignOff ||
    showApproveSignOff ||
    showDeclineSignOff ||
    showRevokeSignOff ||
    showCloseJob ||
    showDeleteJob;

  return (
    <React.Fragment>
      <DropdownMenu
        trigger={({ isOpen, triggerProps }) => (
          <DisabledElementTooltip
            label="Job options button"
            childDisabled={!hasMenuItem}
            content="No options available for this job"
          >
            <Button
              size="small"
              weight="subtle"
              label={
                isPending
                  ? ((
                      <LoadingDots
                        label="pending"
                        size="small"
                        data-testid={makeId(testId, 'pending')}
                      />
                    ) as any)
                  : 'Options'
              }
              iconAfter={isOpen ? ChevronUpIcon : ChevronDownIcon}
              disabled={!hasMenuItem}
              {...triggerProps}
              data-testid={makeId(testId, 'button')}
            />
          </DisabledElementTooltip>
        )}
        data-testid={makeId(testId, 'dropdown-menu')}
      >
        {showRequestSignOff && (
          <MenuItem
            icon={MailIcon}
            label="Request to sign off"
            tone="active"
            onClick={async () => {
              if (isPending) return;
              setIsPending(true);

              await jobSignOffRequest(job);

              setIsPending(false);
            }}
            data-testid={makeId(testId, 'request-to-sign-off')}
          />
        )}
        {showWithdrawSignOff && (
          <MenuItem
            icon={CornerUpLeftIcon}
            label="Withdraw request"
            tone="active"
            onClick={async () => {
              if (isPending) return;
              setIsPending(true);

              await jobSignOffWithdraw(job);

              setIsPending(false);
            }}
            data-testid={makeId(testId, 'withdraw-request')}
          />
        )}
        {showApproveSignOff && (
          <MenuItem
            icon={CheckIcon}
            label="Approve"
            tone="active"
            onClick={async () => {
              if (isPending) return;
              setIsPending(true);

              await jobSignOffApprove({
                variables: { cloudId: job.cloudId || '' },
              });

              setIsPending(false);
            }}
            data-testid={makeId(testId, 'approve')}
          />
        )}
        {showDeclineSignOff && (
          <MenuItem
            icon={XIcon}
            label="Decline"
            tone="active"
            onClick={async () => {
              if (isPending) return;
              setIsPending(true);

              await jobSignOffDecline(job);

              setIsPending(false);
            }}
            data-testid={makeId(testId, 'decline')}
          />
        )}
        {showRevokeSignOff && (
          <MenuItem
            icon={CornerUpLeftIcon}
            label="Revoke approval"
            tone="active"
            onClick={async () => {
              if (isPending) return;
              setIsPending(true);

              await jobSignOffRevoke(job);

              setIsPending(false);
            }}
            data-testid={makeId(testId, 'revoke-approval')}
          />
        )}
        {showCloseJob && (
          <MenuItem
            icon={CheckCircleIcon}
            label="Close job"
            tone="active"
            onClick={async () => {
              if (isPending) return;
              setIsPending(true);

              await updateJobStatus({ job, status: closedJobStatus });

              setIsPending(false);
            }}
            data-testid={makeId(testId, 'close-job')}
          />
        )}
        {showDeleteJob && (
          <MenuItem
            icon={Trash2Icon}
            label="Delete job"
            tone="critical"
            onClick={async () => {
              if (isPending) return;
              setIsPending(true);

              const deleteResult = await deleteJob(job);
              setIsPending(false);
              if (deleteResult) {
                doClose({ bypassDirtyCheck: true });
              }
            }}
            data-testid={makeId(testId, 'delete-job')}
          />
        )}
      </DropdownMenu>
      <JobSignOffRequestDialog />
      <JobSignOffWithdrawDialog />
      <JobSignOffDeclineDialog />
      <JobSignOffRevokeDialog />
      <UpdateJobStatusDialog />
      <DeleteJobDialog />
    </React.Fragment>
  );
};
